import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const SESSION_POLL_INTERVAL  = 15 * 60 * 1000; // 15 min liveness check
const INACTIVITY_TIMEOUT     = 30 * 60 * 1000; // 30 min idle → auto sign-out
const INACTIVITY_EVENTS      = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [role,    setRole]    = useState(undefined);
  const inactivityTimer = useRef(null);
  const signOutRef      = useRef(null); // forward ref so the timer closure is stable

  const fetchRole = async (userId) => {
    if (!userId) { setRole(null); return; }
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .single();
    setRole(data?.role ?? null);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      fetchRole(data.session?.user?.id);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      fetchRole(s?.user?.id);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  // Periodic liveness check — detects expired refresh tokens in long-open tabs.
  useEffect(() => {
    if (!session) return;
    const id = setInterval(() => supabase.auth.getSession(), SESSION_POLL_INTERVAL);
    return () => clearInterval(id);
  }, [session]);

  const signIn = async (email, password) => {
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.data?.session) {
      await supabase.from('audit_logs').insert({
        user_id:       result.data.session.user.id,
        user_email:    email,
        action:        'login',
        resource_type: 'session',
        user_agent:    navigator.userAgent,
      });
    } else if (result.error) {
      await supabase.rpc('log_failed_login', {
        p_email:      email,
        p_user_agent: navigator.userAgent,
      });
    }
    return result;
  };

  const signOut = useCallback(async () => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (s) {
      await supabase.from('audit_logs').insert({
        user_id:       s.user.id,
        user_email:    s.user.email,
        action:        'logout',
        resource_type: 'session',
        user_agent:    navigator.userAgent,
      });
    }
    return supabase.auth.signOut();
  }, []);

  // Keep the ref current so the timer closure always calls the latest signOut.
  useEffect(() => { signOutRef.current = signOut; }, [signOut]);

  // Inactivity auto-logout — only active while a session exists.
  useEffect(() => {
    if (!session) {
      clearTimeout(inactivityTimer.current);
      return;
    }

    const reset = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => signOutRef.current?.(), INACTIVITY_TIMEOUT);
    };

    reset(); // start the clock immediately on login
    INACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    return () => {
      clearTimeout(inactivityTimer.current);
      INACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, reset));
    };
  }, [session]);

  return (
    <AuthContext.Provider value={{ session, role, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
